// frontend/src/components/features/Marketplace.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Globe, ArrowLeft } from "lucide-react";
import { API_BASE } from "../../config/api";
import { authService } from "../../services/auth";
import { httpRequest } from "../../utils/httpClient";
import { useSidebarZIndex } from "../../hooks/useSidebarZIndex";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { AMAZON_ICON, WALLAPOP_ICON, BGA_ICON, BOARDGAMEPRICES_ICON, SHOPPING_ICON } from "../../constants/icons";
import logger from '../../utils/logger';
import { detectRegion, getCurrencyForCountry } from "../../utils/regionDetection";

const CURRENCY_SYMBOL = { USD: "$", EUR: "€", GBP: "£", CAD: "C$", AUD: "A$" };

function Marketplace({ gameId, gameName, onClose }) {
  const { trackClick } = useAnalytics();
  const [listings, setListings] = useState([]);
  const [individualListings, setIndividualListings] = useState([]);
  const [marketplaceMeta, setMarketplaceMeta] = useState({ ebay_configured: true, ebay_results: 0 });
  const [newCopy, setNewCopy] = useState(null);
  const [usedCopy, setUsedCopy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [languageDependence, setLanguageDependence] = useState(null);

  // By Price panel visibility (hidden until "View copies" clicked)
  const [showByPrice, setShowByPrice] = useState(false);

  // User settings — populated from IP detection, overridable via settings panel
  const [userCountry, setUserCountry] = useState("US");
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const [regionDetected, setRegionDetected] = useState(false);

  // Dynamic z-index management for sidebar
  const { zIndex } = useSidebarZIndex('marketplace');

  // Click outside handler to close sidebar
  const marketplaceRef = useClickOutside(onClose);

  // Track and open outbound marketplace links
  const handleSourceClick = useCallback((url, platform) => {
    trackClick('source_click', { platform, game_id: gameId, game_name: gameName });
    trackClick('followup_action', { action: 'buy_click', platform, game_id: gameId, game_name: gameName });
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [trackClick, gameId, gameName]);

  // Detect region from IP on mount (once per session, cached)
  useEffect(() => {
    if (regionDetected) return;
    detectRegion().then(({ country, currency }) => {
      setUserCountry(country);
      setBaseCurrency(currency);
      setRegionDetected(true);
    });
  }, [regionDetected]);

  // Update base currency automatically when country changes in settings panel
  useEffect(() => {
    if (!regionDetected) return;
    setBaseCurrency(getCurrencyForCountry(userCountry));
  }, [userCountry, regionDetected]);

  // Fetch marketplace listings whenever region/currency is ready
  useEffect(() => {
    const fetchMarketplaceListings = async () => {
      if (!gameId) {
        setError("Invalid game ID");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.append("game_id", gameId);
        params.append("countries", userCountry);
        params.append("user_country", userCountry);
        params.append("base_currency", baseCurrency);

        const res = await httpRequest(`${API_BASE}/marketplace/search?${params.toString()}`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        });

        if (res.ok) {
          const data = await res.json();
          setListings(data.listings || []);
          setIndividualListings(data.individual_listings || []);
          setNewCopy(data.new_copy || null);
          setUsedCopy(data.used_copy || null);
          setLanguageDependence(data.language_dependence || null);
          setMarketplaceMeta({
            ebay_configured: data.ebay_configured !== false,
            ebay_results: data.ebay_results || 0,
          });
        } else {
          const errorText = await res.text().catch(() => "Unknown error");
          logger.error("Marketplace API error:", res.status, errorText);
          setError(`Failed to fetch marketplace listings (${res.status})`);
        }
      } catch (err) {
        logger.error("Marketplace fetch error:", err);
        setError(`Error loading marketplace data: ${err.message || "Network error"}`);
      } finally {
        setLoading(false);
      }
    };

    if (gameId && regionDetected) {
      trackClick('find_copy_lookup', { game_id: gameId, game_name: gameName, country: userCountry });
      fetchMarketplaceListings();
    }
  }, [gameId, userCountry, baseCurrency, gameName, trackClick, regionDetected]);

  const getPlatformIcon = (platform) => {
    const platformLower = platform?.toLowerCase() || "";
    if (platformLower.includes("bga") || platformLower.includes("board game arena")) return BGA_ICON;
    if (platformLower.includes("boardgameprices")) return BOARDGAMEPRICES_ICON;
    if (platformLower.includes("amazon")) return AMAZON_ICON;
    if (platformLower.includes("ebay")) return null;
    if (platformLower.includes("geekmarket")) return null;
    if (platformLower.includes("wallapop")) return WALLAPOP_ICON;
    return SHOPPING_ICON;
  };

  // Individual listings for By Price tab come directly from the backend (pre-grouping, sorted)
  const getIndividualListings = () =>
    individualListings.map(listing => {
      let displayPlatform = listing.platform;
      // Append region for Amazon listings so users know which regional Amazon they're visiting
      if (displayPlatform && displayPlatform.toLowerCase() === 'amazon' && listing.country) {
        displayPlatform = `Amazon (${listing.country})`;
      }
      return {
        ...listing,
        displayPrice: listing.price_in_base_currency || 0,
        displayPlatform,
        displayUrl: listing.url,
        displayPriceInfo: listing.price_in_base_currency !== null && listing.price_in_base_currency !== undefined
          ? `${CURRENCY_SYMBOL[baseCurrency] || baseCurrency}${listing.price_in_base_currency.toFixed(2)}`
          : "N/A",
      };
    });

  const currencySymbol = CURRENCY_SYMBOL[baseCurrency] || baseCurrency;

  // Shared tile style for new copy / used copy cards
  const copyTileStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    padding: "0.75rem",
    borderRadius: "6px",
    border: "1px solid var(--border-color, #ddd)",
    margin: "0.75rem 1rem",
  };

  const copyActionButtonStyle = {
    marginTop: "0.25rem",
    padding: "0.5rem 1rem",
    backgroundColor: "var(--pista-green-primary)",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "0.9rem",
    alignSelf: "flex-end",
  };

  return (
    <div ref={marketplaceRef} className="marketplace-sidebar" data-game-id={gameId} data-game-name={gameName} role="dialog" aria-modal="true" aria-labelledby="marketplace-title" style={{ zIndex }}>

      {/* Header row: title, close */}
      <div className="marketplace-header">
        <h3 id="marketplace-title">Access: {gameName}</h3>
        <button onClick={onClose} className="marketplace-close" aria-label="Close marketplace">
          ×
        </button>
      </div>


      {/* Language Dependence Badge */}
      {languageDependence && (
        <div style={{
          padding: "0.5rem 1rem",
          backgroundColor: languageDependence.color || "#999",
          color: languageDependence.level >= 4 ? "white" : "black",
          fontSize: "0.8rem",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          whiteSpace: "nowrap",
          flexWrap: "nowrap",
        }} title={languageDependence.label}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
            <Globe size={14} /> Language:
          </span>
          <span>{languageDependence.short_label} ({languageDependence.level}/5)</span>
        </div>
      )}

      {/* BGA online play button */}
      {!loading && (() => {
        const bgaListing = listings.find(l => l.platform?.toLowerCase().includes("bga"));
        if (!bgaListing?.url) return null;
        const priceText = bgaListing.price_label ||
          (bgaListing.bga_access === "premium_required"
            ? `${currencySymbol}${bgaListing.premium_price?.toFixed(2) || "14.99"}/month`
            : "Free");
        return (
          <div style={{ padding: "0.5rem 1rem", borderBottom: "1px solid var(--border-color, #ddd)" }}>
            <button
              onClick={() => handleSourceClick(bgaListing.url, 'bga')}
              style={{
                width: "100%",
                padding: "0.5rem 1rem",
                backgroundColor: "var(--pista-green-primary)",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "0.9rem",
              }}
            >
              Play on BGA — {priceText}
            </button>
          </div>
        );
      })()}

      {/* Main content */}
      {loading ? (
        <div className="marketplace-loading">Loading listings...</div>
      ) : error ? (
        <div className="marketplace-error">{error}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>

          {/* ── By Price panel (shown when showByPrice = true) ── */}
          {showByPrice ? (
            <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
              {/* Back button */}
              <div style={{ padding: "0.5rem 1rem", borderBottom: "1px solid var(--border-color, #ddd)", flexShrink: 0 }}>
                <button
                  onClick={() => setShowByPrice(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--pista-green-primary)",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    padding: "0.25rem 0",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                  aria-label="Back to summary"
                >
                  <ArrowLeft size={14} /> Back
                </button>
              </div>

              {/* Individual listings */}
              <div className="marketplace-listings">
                {getIndividualListings().length === 0 ? (
                  <div className="marketplace-empty">No individual listings found.</div>
                ) : (
                  getIndividualListings().map((listing, idx) => {
                    const platformLower = listing.displayPlatform?.toLowerCase() || "";
                    const marketplaceName = listing.marketplace_name?.toLowerCase() || "";
                    const isBga = platformLower.includes("bga") || marketplaceName === "bga";
                    const conditionText = isBga
                      ? "digital"
                      : listing.condition
                        ? listing.condition.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
                        : null;

                    return (
                      <div
                        key={`price-${idx}`}
                        className="marketplace-listing"
                        style={{ marginBottom: "0.5rem" }}
                      >
                        <div className="listing-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span className="listing-platform">
                            {getPlatformIcon(listing.displayPlatform)} {listing.displayPlatform}
                          </span>
                          {conditionText && (
                            <span style={{
                              fontSize: "0.75rem",
                              color: "var(--pista-green-primary)",
                              backgroundColor: "var(--bg-secondary, transparent)",
                              border: "1px solid var(--pista-green-primary)",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontWeight: "bold",
                            }}>
                              {conditionText}
                            </span>
                          )}
                        </div>
                        <div className="listing-price" style={{ fontWeight: "bold", fontSize: "1rem", margin: "0.25rem 0" }}>
                          {listing.displayPriceInfo}
                        </div>
                        {listing.displayUrl && (
                          <button
                            onClick={() => isBga
                              ? handleSourceClick(listing.displayUrl, 'bga')
                              : handleSourceClick(listing.displayUrl, platformLower)
                            }
                            className="listing-link"
                            style={{
                              marginTop: "0.5rem",
                              padding: "0.4rem 1rem",
                              backgroundColor: "var(--pista-green-primary)",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                              width: "100%",
                              textAlign: "center",
                            }}
                            aria-label={`View listing on ${listing.displayPlatform}`}
                          >
                            View
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            /* ── Default summary view ── */
            <div style={{ overflowY: "auto", flex: 1 }}>

              {/* New copy tile */}
              {newCopy && (
                <div style={copyTileStyle}>
                  <div style={{ fontWeight: "bold", fontSize: "0.85rem", color: "var(--text-secondary, #666)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    New copy price
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
                    {(newCopy.price_range_in_base || newCopy.price_range) && (
                      <span style={{ fontSize: "1.3rem", fontWeight: "bold", color: "var(--text-primary, #333)" }}>
                        {newCopy.price_range_in_base || newCopy.price_range}
                      </span>
                    )}
                  </div>
                  {newCopy.platform && (
                    <div style={{ fontSize: "0.8rem", color: "var(--text-secondary, #666)" }}>
                      {getPlatformIcon(newCopy.platform)} {newCopy.platform}
                    </div>
                  )}
                  <button
                    onClick={() => newCopy.url && window.open(newCopy.url, "_blank", "noopener,noreferrer")}
                    style={copyActionButtonStyle}
                    aria-label={`Go to ${newCopy.platform} listing`}
                    disabled={!newCopy.url}
                  >
                    Go
                  </button>
                </div>
              )}

              {/* Used copy tile */}
              {(() => {
                const savingsAmount =
                  usedCopy &&
                  newCopy?.price_in_base > 0
                    ? newCopy.price_in_base - usedCopy.median_price
                    : null;
                const showSavings = savingsAmount !== null && savingsAmount > 0;

                return (
                  <div style={copyTileStyle}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ fontWeight: "bold", fontSize: "0.85rem", color: "var(--text-secondary, #666)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Used copy price
                      </div>
                      {showSavings && (
                        <div style={{
                          backgroundColor: "var(--pista-green-primary)",
                          color: "white",
                          fontSize: "0.72rem",
                          fontWeight: "bold",
                          padding: "0.2rem 0.55rem",
                          borderRadius: "999px",
                          whiteSpace: "nowrap",
                        }}>
                          Save {currencySymbol}{savingsAmount.toFixed(2)} vs new
                        </div>
                      )}
                    </div>
                    {usedCopy ? (
                      <>
                        <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
                          <span style={{ fontSize: "1.3rem", fontWeight: "bold", color: "var(--text-primary, #333)" }}>
                            {currencySymbol}{usedCopy.median_price.toFixed(2)}
                          </span>
                          <span style={{ fontSize: "0.8rem", color: "var(--text-secondary, #666)" }}>
                            median · {usedCopy.listing_count} {usedCopy.listing_count === 1 ? "listing" : "listings"}
                          </span>
                        </div>
                        {usedCopy.min_price !== null && usedCopy.min_price !== undefined && usedCopy.min_price < usedCopy.median_price && (
                          <div style={{ fontSize: "0.8rem", color: "var(--text-secondary, #666)" }}>
                            Starting from {currencySymbol}{usedCopy.min_price.toFixed(2)}
                          </div>
                        )}
                        <button
                          onClick={() => setShowByPrice(true)}
                          style={copyActionButtonStyle}
                          aria-label="View used copy listings"
                        >
                          View copies
                        </button>
                      </>
                    ) : (
                      <div style={{ fontSize: "0.85rem", color: "var(--text-secondary, #666)", fontStyle: "italic" }}>
                        No used listings found
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Empty state for no listings at all */}
              {listings.length === 0 && !newCopy && !usedCopy && (
                <div className="marketplace-empty">
                  No listings found for this game.
                  {!marketplaceMeta.ebay_configured && (
                    <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "var(--text-secondary, #666)" }}>
                      eBay is not configured. Set EBAY_APP_ID to enable results.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Marketplace;
